import { useCallback, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams } from "@tanstack/react-router"
import {
	IconPen2Outline18 as Edit2,
	IconFolderOutline18 as Folder,
	IconPlusOutline18 as Plus,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"

import { pro } from "@productlane/lib/src/browser"
import {
	AlertDialog,
	Button,
	IconButton,
	Loader,
	NucleoFillIcon,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { SnippetInput } from "./snippets-settings-page"
import { CreateSnippetDialog } from "@/components/editor/snippets/create-snippet-dialog"
import { SnippetFolderDialog } from "@/components/editor/snippets/snippet-folder-dialog"
import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { UpgradeButton } from "@/components/upgrade-button"
import { analytics } from "@/lib/analytics"
import { useRequiredPlans } from "@/lib/plans"
import { mutateOk, useZero } from "@/lib/zero"
import { SettingsHeader } from "../settings-header"
import {
	emptyInput,
	SnippetCard,
	UNCATEGORIZED_FOLDER,
} from "./snippets-settings-page"

export const SnippetFolderPage = () => {
	const { passes: hasRequiredPlan } = useRequiredPlans(pro.andUp)
	const zero = useZero()
	const navigate = useNavigate()
	const { folderId } = useParams({ strict: false })

	const [snippets] = useQuery(queries.snippets())
	const [folders] = useQuery(queries.snippetFolders())

	const isUncategorized = folderId === UNCATEGORIZED_FOLDER
	const folder = isUncategorized
		? undefined
		: folders.find((f) => f.id === folderId)

	const folderSnippets = useMemo(
		() =>
			snippets.filter((snippet) =>
				isUncategorized ? !snippet.folderId : snippet.folderId === folderId,
			),
		[snippets, folderId, isUncategorized],
	)

	const prefilledInput = useRef<SnippetInput>(emptyInput)

	const [isDialogOpen, setDialogOpen] = useState(false)
	const [isRenameOpen, setRenameOpen] = useState(false)
	const [isDeleteFolderOpen, setDeleteFolderOpen] = useState(false)
	const [snippetIdToDelete, setSnippetIdToDelete] = useState<string | boolean>(
		false,
	)

	const createSnippet = useCallback(() => {
		prefilledInput.current = {
			...emptyInput,
			folderId: isUncategorized ? null : (folderId ?? null),
		}
		setDialogOpen(true)
		analytics.track("create-snippet")
	}, [folderId, isUncategorized])

	const closeDialog = useCallback(() => {
		setDialogOpen(false)
		analytics.track("close-snippet-dialog")
	}, [])

	const editSnippet = useCallback(
		(existingInput: SnippetInput) => () => {
			prefilledInput.current = existingInput
			setDialogOpen(true)
			analytics.track("edit-snippet")
		},
		[],
	)

	const moveSnippet = useCallback(
		(id: string) => (newFolderId: string | null) => {
			void mutateOk(
				zero.mutate(mutators.snippets.update({ id, folderId: newFolderId })),
			)
			analytics.track("snippet-move-to-folder")
		},
		[zero],
	)

	if (!isUncategorized && !folder) {
		return (
			<>
				<MetaInfo title="Macros" />
				<SettingsHeader title="Macros" subtitle="Loading…" />
				<div className="flex items-center justify-center py-8">
					<Loader />
				</div>
			</>
		)
	}

	const title = isUncategorized ? "Uncategorized" : (folder?.name ?? "")

	return (
		<>
			<MetaInfo title={`${title} · Macros`} />

			<CreateSnippetDialog
				data={prefilledInput.current}
				isOpen={isDialogOpen}
				onOpenChange={closeDialog}
			/>

			{folder && (
				<SnippetFolderDialog
					isOpen={isRenameOpen}
					onOpenChange={setRenameOpen}
					folder={folder}
				/>
			)}

			<div className="space-y-4">
				<SettingsHeader
					title={title}
					className="[&>div]:items-start"
					titlePrefix={
						folder?.icon ? (
							<NucleoFillIcon
								icon={folder.icon}
								className="h-6 w-6 flex-none text-secondary"
							/>
						) : (
							<Folder className="h-6 w-6 flex-none text-secondary" />
						)
					}
					subtitle={
						folderSnippets.length > 0
							? `${folderSnippets.length} ${folderSnippets.length === 1 ? "macro" : "macros"}`
							: ""
					}
					action={
						hasRequiredPlan && (
							<RoleCheck not={["VIEWER"]}>
								<div className="flex items-center gap-2">
									<Button Icon={Plus} onClick={createSnippet}>
										Add
									</Button>
									{folder && (
										<DropdownMenu
											trigger={
												<IconButton
													Icon={MoreVertical}
													variant="ghost"
													iconClassName="fill-current"
												/>
											}
											contentProps={{ side: "bottom", align: "end" }}
										>
											<DropdownMenuGroup>
												<DropdownMenuItem
													Icon={Edit2}
													onClick={() =>
														setTimeout(() => setRenameOpen(true), 0)
													}
												>
													Edit
												</DropdownMenuItem>
												<DropdownMenuItem
													Icon={Trash}
													onClick={() => setDeleteFolderOpen(true)}
												>
													Delete
												</DropdownMenuItem>
											</DropdownMenuGroup>
										</DropdownMenu>
									)}
								</div>
							</RoleCheck>
						)
					}
				/>

				{hasRequiredPlan ? (
					<>
						{folderSnippets.length > 0 ? (
							<div className="space-y-2">
								{folderSnippets.map((snippet) => (
									<SnippetCard
										key={`snippet-${snippet.id}`}
										snippet={snippet}
										folders={folders}
										onEdit={editSnippet({
											id: snippet.id,
											title: snippet.title,
											html: snippet.html,
											folderId: snippet.folderId ?? null,
										})}
										onDelete={() => {
											setSnippetIdToDelete(snippet.id)
											analytics.track("delete-snippet")
										}}
										onMove={moveSnippet(snippet.id)}
									/>
								))}
							</div>
						) : (
							<p className="text-sm text-tertiary">
								No snippets in this folder yet.
							</p>
						)}

						<AlertDialog
							isOpen={!!snippetIdToDelete}
							onOpenChange={setSnippetIdToDelete}
							confirmText="Delete"
							title="Are you sure?"
							description="Do you really want to delete this macro?"
							onCancel={() => setSnippetIdToDelete(false)}
							onConfirm={async () => {
								if (typeof snippetIdToDelete !== "string") return
								await mutateOk(
									zero.mutate(
										mutators.snippets.delete({
											id: snippetIdToDelete,
										}),
									),
								)
								analytics.track("snippet-delete")
							}}
						/>

						<AlertDialog
							isOpen={isDeleteFolderOpen}
							onOpenChange={setDeleteFolderOpen}
							confirmText="Delete"
							title="Delete folder?"
							description="Macros in this folder are kept and become uncategorized."
							onCancel={() => setDeleteFolderOpen(false)}
							onConfirm={async () => {
								if (!folder) return
								await mutateOk(
									zero.mutate(
										mutators.snippetFolders.delete({ id: folder.id }),
									),
								)
								analytics.track("snippet-folder-delete")
								void navigate({ to: "/settings/macros" })
							}}
						/>
					</>
				) : (
					<UpgradeButton required={pro.andUp} />
				)}
			</div>
		</>
	)
}
